import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useReadySubstrates,
  useAvailableSpawnWithBags,
  useInoculationMutations
} from '@/hooks/useInoculation'
import { Sprout, AlertTriangle } from 'lucide-react'
import SpawnSelectionTable from './SpawnSelectionTable'
import SubstrateAllocationTable from './SubstrateAllocationTable'
import SpawnRateDisplay from './SpawnRateDisplay'
import { toLocalDateString } from '@/utils/dateUtils'

export default function InoculationFormV2({ isOpen, onClose, onSave }) {
  // State
  const [selectedBagIds, setSelectedBagIds] = useState(new Set())
  const [substrateAllocations, setSubstrateAllocations] = useState({})
  const [fillAllocations, setFillAllocations] = useState({})
  const [error, setError] = useState(null)

  // Data hooks — fetch all colonised spawn (no strain filter)
  const { spawnBatches, loading: spawnLoading } = useAvailableSpawnWithBags(null)
  const { substrates: readySubstrates, loading: substratesLoading } = useReadySubstrates()
  const { createInoculation, loading: saving } = useInoculationMutations()

  // Derive selected strain from selected bags
  const selectedStrainId = useMemo(() => {
    if (selectedBagIds.size === 0 || !spawnBatches) return null
    for (const batch of spawnBatches) {
      for (const bag of (batch.available_bags || [])) {
        if (selectedBagIds.has(bag.id)) {
          return batch.strain_id
        }
      }
    }
    return null
  }, [selectedBagIds, spawnBatches])

  // Filter spawn table: if a strain is selected, only show that strain
  const visibleSpawnBatches = useMemo(() => {
    if (!spawnBatches) return []
    if (!selectedStrainId) return spawnBatches
    return spawnBatches.filter(b => b.strain_id === selectedStrainId)
  }, [spawnBatches, selectedStrainId])

  // Calculate selected bags grouped by batch
  const selectedBagsByBatch = useMemo(() => {
    if (!spawnBatches || selectedBagIds.size === 0) return {}
    const byBatch = {}
    for (const batch of spawnBatches) {
      for (const bag of (batch.available_bags || [])) {
        if (selectedBagIds.has(bag.id)) {
          byBatch[batch.id] = byBatch[batch.id] || []
          byBatch[batch.id].push(bag.id)
        }
      }
    }
    return byBatch
  }, [spawnBatches, selectedBagIds])

  // Total spawn weight in grams
  const totalSpawnWeightGrams = useMemo(() => {
    if (!spawnBatches || selectedBagIds.size === 0) return 0
    let total = 0
    for (const batch of spawnBatches) {
      for (const bag of (batch.available_bags || [])) {
        if (selectedBagIds.has(bag.id)) {
          total += (batch.bag_weight_kg || 1) * 1000
        }
      }
    }
    return total
  }, [spawnBatches, selectedBagIds])

  // Total substrate weight in grams + total bags allocated
  const { totalSubstrateWeightGrams, totalSubstrateBags } = useMemo(() => {
    let weightGrams = 0
    let bags = 0

    // Legacy batch allocations
    for (const [batchId, qty] of Object.entries(substrateAllocations)) {
      const q = parseInt(qty) || 0
      if (q > 0) {
        const batch = readySubstrates.find(b => b.id === batchId)
        const weightKg = batch?.block_format_weight_kg || 4.5
        weightGrams += q * weightKg * 1000
        bags += q
      }
    }

    // Per-fill allocations
    for (const [fillId, entry] of Object.entries(fillAllocations)) {
      const q = parseInt(entry.quantity) || 0
      if (q > 0) {
        const batch = readySubstrates.find(b => b.id === entry.batchId)
        const fill = batch?.fills?.find(f => f.id === fillId)
        const weightKg = fill?.block_format?.weight_kg || batch?.block_format_weight_kg || 4.5
        weightGrams += q * weightKg * 1000
        bags += q
      }
    }

    return { totalSubstrateWeightGrams: weightGrams, totalSubstrateBags: bags }
  }, [substrateAllocations, fillAllocations, readySubstrates])

  // Spawn rate calculations
  const spawnRatePercent = totalSubstrateWeightGrams > 0
    ? (totalSpawnWeightGrams / totalSubstrateWeightGrams) * 100
    : 0

  const gramsPerBag = totalSubstrateBags > 0
    ? totalSpawnWeightGrams / totalSubstrateBags
    : 0

  const hasSelections = selectedBagIds.size > 0 && totalSubstrateBags > 0

  // Toggle a single bag selection
  const handleBagToggle = (bagId) => {
    setSelectedBagIds(prev => {
      const next = new Set(prev)
      if (next.has(bagId)) {
        next.delete(bagId)
      } else {
        next.add(bagId)
      }
      return next
    })
  }

  // Substrate allocation handlers
  const handleSubstrateAllocation = (batchId, quantity) => {
    setSubstrateAllocations(prev => {
      const next = { ...prev }
      if (quantity > 0) {
        next[batchId] = quantity
      } else {
        delete next[batchId]
      }
      return next
    })
  }

  const handleFillAllocation = (fillId, batchId, quantity) => {
    setFillAllocations(prev => {
      const next = { ...prev }
      if (quantity > 0) {
        next[fillId] = { batchId, quantity }
      } else {
        delete next[fillId]
      }
      return next
    })
  }

  // Submit
  const handleSubmit = async () => {
    setError(null)

    if (selectedBagIds.size === 0) {
      setError('Select at least one spawn bag')
      return
    }
    if (totalSubstrateBags === 0) {
      setError('Allocate at least one substrate bag')
      return
    }
    if (!selectedStrainId) {
      setError('Could not determine strain from selected spawn')
      return
    }

    try {
      // Build allocations array
      const legacyAllocations = Object.entries(substrateAllocations).map(([batchId, quantity]) => ({
        substrate_batch_id: batchId,
        blocks_allocated: parseInt(quantity)
      }))

      const perFillAllocations = Object.entries(fillAllocations).map(([fillId, entry]) => ({
        substrate_batch_id: entry.batchId,
        blocks_allocated: parseInt(entry.quantity),
        fill_id: fillId
      }))

      const allocations = [...legacyAllocations, ...perFillAllocations]

      // Build spawn usage array
      const spawnUsage = Object.entries(selectedBagsByBatch).map(([batchId, bagIds]) => ({
        spawn_batch_id: batchId,
        bag_ids: bagIds
      }))

      await createInoculation({
        strain_id: selectedStrainId,
        event_date: toLocalDateString(new Date()),
        spawn_rate_percent: parseFloat(spawnRatePercent.toFixed(2)),
        allocations,
        spawn_usage: spawnUsage
      })

      onSave?.()
    } catch (err) {
      setError(err.message || 'Failed to create inoculation event')
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sprout className="h-5 w-5" />
              Record Inoculation
            </DialogTitle>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Spawn Selection */}
          <div>
            <h3 className="text-sm font-medium mb-2">Spawn</h3>
            <div className="border rounded-lg overflow-hidden">
              <SpawnSelectionTable
                visibleBatches={visibleSpawnBatches}
                selectedBagIds={selectedBagIds}
                onBagToggle={handleBagToggle}
                loading={spawnLoading}
              />
            </div>
          </div>

          {/* Substrate Allocation */}
          <div>
            <h3 className="text-sm font-medium mb-2">Substrate</h3>
            <div className="border rounded-lg overflow-hidden">
              {substratesLoading ? (
                <div className="flex items-center justify-center py-8 text-content-secondary text-sm">
                  Loading substrates...
                </div>
              ) : (
                <SubstrateAllocationTable
                  substrates={readySubstrates}
                  substrateAllocations={substrateAllocations}
                  fillAllocations={fillAllocations}
                  onSubstrateAllocation={handleSubstrateAllocation}
                  onFillAllocation={handleFillAllocation}
                />
              )}
            </div>
          </div>

          {/* Spawn Rate */}
          <SpawnRateDisplay
            spawnRatePercent={spawnRatePercent}
            gramsPerBag={gramsPerBag}
            hasSelections={hasSelections}
          />

          <DialogFooter className="flex justify-between items-center">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Creating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  )
}
